import * as React from 'react'

import { Menu as MenuPrimitive } from '@base-ui/react/menu'

import type { DropdownMenuContent as BaseDropdownMenuContent } from '@/components/ui/dropdown-menu'
import {
  DropdownMenuItem as BaseDropdownMenuItem,
  DropdownMenuRadioItem as BaseDropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

type DropdownMenuItemProps = React.ComponentProps<typeof BaseDropdownMenuItem>

function DropdownMenuItem({ className, ...props }: DropdownMenuItemProps) {
  return (
    <BaseDropdownMenuItem
      className={cn('cursor-pointer', className)}
      {...props}
    />
  )
}

type DropdownMenuRadioItemProps = React.ComponentProps<
  typeof BaseDropdownMenuRadioItem
>

function DropdownMenuRadioItem({
  className,
  ...props
}: DropdownMenuRadioItemProps) {
  return (
    <BaseDropdownMenuRadioItem
      className={cn('cursor-pointer', className)}
      {...props}
    />
  )
}

interface DropdownMenuContentProps extends React.ComponentProps<
  typeof BaseDropdownMenuContent
> {
  /**
   * When true, disables the Portal and renders the content inline.
   * Useful when DropdownMenu is inside a Drawer/Dialog to prevent click-outside issues.
   */
  disablePortal?: boolean
}

function DropdownMenuContent({
  className,
  align = 'start',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  disablePortal = false,
  ...props
}: DropdownMenuContentProps) {
  const [inlineContainer, setInlineContainer] =
    React.useState<HTMLElement | null>(null)

  const content = (
    <MenuPrimitive.Positioner
      className="isolate z-50 outline-none"
      align={align}
      alignOffset={alignOffset}
      side={side}
      sideOffset={sideOffset}
    >
      <MenuPrimitive.Popup
        data-slot="dropdown-menu-content"
        className={cn(
          'data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 data-closed:zoom-out-95 data-open:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 bg-popover text-popover-foreground z-50 max-h-(--available-height) w-(--anchor-width) min-w-32 origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-md p-1 shadow-md ring-1 duration-100 outline-none data-closed:overflow-hidden',
          'w-max max-w-(--available-width)',
          className,
        )}
        {...props}
      />
    </MenuPrimitive.Positioner>
  )

  if (disablePortal) {
    return (
      <span ref={setInlineContainer} style={{ display: 'contents' }}>
        <MenuPrimitive.Portal container={inlineContainer}>
          {content}
        </MenuPrimitive.Portal>
      </span>
    )
  }

  return <MenuPrimitive.Portal>{content}</MenuPrimitive.Portal>
}

export { DropdownMenuContent, DropdownMenuItem, DropdownMenuRadioItem }
